import styles from "./Register.module.scss";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Form } from "antd";
import FormItemInput from "../../components/ui/FormItemInput/FormItemInput";
import FormItemInputPassword from "../../components/ui/FormItemInputPassword/FormItemInputPassword";
import ImagesWrapper from "../../components/shared/ImagesWrapper/ImagesWrapper";
import CustomButton from "../../components/ui/CustomButton/CustomButton";
import { AuthService } from "../../services/auth.service";
import { IRegisterModel, IUser } from "../../models/User";
import { emailValidation, passwordValidation } from "../../helper/auth";
import { useEffect, useState } from "react";
import { getLanguageFromLocal } from "../../helper/language";
import { openErrorMessage } from "../../store/message/messageSlice";
import useAppDispatch from "../../hooks/useAppDispatch";
import GoogleSignInButton from "../../components/GoogleSignInButton/GoogleSignInButton";
import FacebookSignInButton from "../../components/FacebookSignInButton/FacebookSignInButton";
import AppleSignInButton from "../../components/AppleSignInButton/AppleSignInButton";
import { handleError } from "../../helper/errorHandler";

const Register = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const [referralToken, setReferralToken] = useState<string>("");

  useEffect(() => {
    if (location.search) {
      const searchParams = new URLSearchParams(location.search);
      const token = searchParams.get("otp-token");
      const referToken = searchParams.get("refer");
      if (token) {
        verifyToken(token);
        searchParams.delete("otp-token");
      }
      if (referToken) {
        setReferralToken(referToken);
      }
    }
  }, [location]);

  const verifyToken = async (token: string) => {
    try {
      const response = await AuthService.verifyOtpToken(token);
      if (response?.status === 200) {
        // TODO navigate to success registry page
        // navigate("/login?success-message=UserRegisterSuccessfully");
        navigate("/success-pre-registry");
      }
    } catch (e) {
      handleError(e, dispatch, t);
      navigate("/register");
    }
  };

  const onFinish = async (values: Omit<IUser, "isVerified" & "id">) => {
    const model: IRegisterModel = {
      email: values.email.trim(),
      metadata: {
        email: values.email.trim(),
        password: values.password?.trim(),
        firstname: values.firstname.trim(),
        lastname: values.lastname.trim(),
        referralToken,
      },
      lang: getLanguageFromLocal(),
    };

    if (!emailValidation(model.email)) {
      dispatch(openErrorMessage(t("PleaseEnterYourEmailCorrectly")));
      return;
    }

    if (model.metadata.password && !passwordValidation(model.metadata.password)) {
      dispatch(openErrorMessage(t("ValidationPassword")));
      return;
    }

    try {
      const response = await AuthService.register(model);
      if (response.status === 200) {
        navigate(
          `/success?title=${t("WelcomeToClubPass")}&description=${t("SuccessForRegisterText")}`
        );
      }
    } catch (e) {
      handleError(e, dispatch, t);
    }
  };

  return (
    <div className={"container"}>
      <div className={styles.container}>
        <ImagesWrapper />
        <div className={styles.box}>
          <h2 className={styles.title}>{t("CreateYourAccount")}</h2>
          <Form
            className={styles.form}
            name="register"
            initialValues={{ remember: true }}
            onFinish={onFinish}
            autoComplete="off"
            layout={"vertical"}
          >
            <FormItemInput name={"firstname"} label={t("FirstName")} rules={{ required: true }} />
            <FormItemInput name={"lastname"} label={t("LastName")} rules={{ required: true }} />
            <FormItemInput name={"email"} label={t("EmailAddress")} rules={{ required: true }} />
            <FormItemInputPassword
              name={"password"}
              label={t("Password")}
              rules={{ required: true }}
            />

            <div className={styles.btnWrapper}>
              <CustomButton text={t("CreateAnAccount")} type={"submit"} typeStyle={"fill"} />
            </div>
          </Form>

          <div className={styles.or}>{t("OrRegisterWith")}</div>

          <div className={styles.btnsWrapper}>
            <GoogleSignInButton />
            <FacebookSignInButton />
            <AppleSignInButton />
          </div>

          <div className={styles.login}>
            {t("AlreadyMemberInClubPass")}
            <Link to={"/login"}> {t("LoginToYourAccount")}</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
